import { Component, DestroyRef, ElementRef, HostListener, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { ROUTE_CONSTANTS } from '@app/core/constants/route.constant';
import { IUserData } from '@app/modules/auth/interfaces/user-data.interface';
import { AuthApiService } from '@app/modules/auth/services/auth-api/auth-api.service';
import { Observable, filter } from 'rxjs';

/** First URL segment → label of the page the mobile menu button shows. */
const PAGE_LABELS: Record<string, string> = {
    [ROUTE_CONSTANTS.HOME]: 'header.home',
    [ROUTE_CONSTANTS.CALENDAR]: 'header.calendar',
    [ROUTE_CONSTANTS.SQUAD]: 'header.squad',
    [ROUTE_CONSTANTS.SESSIONS]: 'header.history',
    [ROUTE_CONSTANTS.USER.ROOT]: 'header.profile',
};

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrl: './header.component.scss',
    standalone: false,
})
export class HeaderComponent {
    readonly ROUTE_CONSTANTS = ROUTE_CONSTANTS;
    readonly user$: Observable<IUserData | null>;

    /** Mobile only (< 768px): the navigation collapses into a dropdown in the top-right corner. */
    isMenuOpen = false;
    currentPageLabel = 'header.menu';

    private readonly router = inject(Router);
    private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

    constructor(private authService: AuthApiService) {
        this.user$ = this.authService.user$;

        this.updatePageLabel(this.router.url);
        this.router.events
            .pipe(
                filter((event): event is NavigationEnd => event instanceof NavigationEnd),
                takeUntilDestroyed(inject(DestroyRef))
            )
            .subscribe((event) => {
                this.isMenuOpen = false;
                this.updatePageLabel(event.urlAfterRedirects);
            });
    }

    toggleMenu(): void {
        this.isMenuOpen = !this.isMenuOpen;
    }

    closeMenu(): void {
        this.isMenuOpen = false;
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        if (this.isMenuOpen && !this.host.nativeElement.querySelector('.mobile-menu')?.contains(event.target as Node)) {
            this.isMenuOpen = false;
        }
    }

    @HostListener('document:keydown.escape')
    onEscape(): void {
        this.isMenuOpen = false;
    }

    private updatePageLabel(url: string): void {
        const segment = url.split(/[?#]/)[0].split('/').filter(Boolean)[0] ?? '';
        this.currentPageLabel = PAGE_LABELS[segment] ?? 'header.menu';
    }
}
